// Hook for fetching application counts grouped by status
// companyId: optional — scopes to a specific company; re-fetches when it changes

import { useState, useEffect, useCallback } from 'react';
import type { ApplicationCountResponse } from '../types';
import { applicationsService, type ApiError } from '../applications.service';

export interface UseApplicationCountResult {
  data: ApplicationCountResponse | null;
  loading: boolean;
  error: ApiError | null;
  refetch: () => void;
}

export function useApplicationCount(companyId?: string): UseApplicationCountResult {
  const [data, setData] = useState<ApplicationCountResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchCount = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await applicationsService.getCount(companyId);
      setData(result);
    } catch (err) {
      setError(err as ApiError);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return { data, loading, error, refetch: fetchCount };
}
